import CurrencyInput from '../../../shared/components/form/CurrencyInput';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useCallback, useEffect } from 'react'
import { Offcanvas } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import Select from 'react-select';
import { toast } from 'sonner';
import { privateApi } from '../../../api/api';
import { Control, FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CreateRateSchema } from '../schema/CreateRateSchema';
import { applyServerValidationErrors } from '../../../shared/utils/serverValidation';

interface CreateRateDialogProps {
    show: boolean;
    onClose: () => void;
    roomId: string;
}

function CreateRateDialog({ show, onClose, roomId }: CreateRateDialogProps) {
    const { register, handleSubmit, control, formState: { errors }, reset, getValues, setError } = useForm({
        resolver: zodResolver(CreateRateSchema),
    });

    useEffect(() => {
        reset({
            room_type: [roomId],
        });
    }, [roomId]);

    const handleClose = () => {
        reset();
        onClose();
    };

    const queryClient = useQueryClient();

    const { mutate, isPending } = useMutation({
        mutationFn: (data) => privateApi.post('/v4/rate-plan', data),
        onSuccess: async () => {
            reset();

            handleClose();

            await queryClient.invalidateQueries({
                queryKey: ['rooms', 'rates'],
            });

            await queryClient.invalidateQueries({
                queryKey: ['rates', roomId],
            });

            toast.success(`Rate created successfully`, {
                id: 'create-rate',
            });
        },
        onError: (error: any) => {
            const handled = applyServerValidationErrors(error, setError, {
                toastId: 'create-rate',
            });

            if (!handled) {
                toast.error(error?.response?.data?.message || 'Failed to create rate', {
                    id: 'create-rate',
                });
            }
        },
    });

    const onSubmit = useCallback((data: any) => {
        toast.loading('Creating rate...', { id: 'create-rate' });
        mutate({
            ...data,
            room_type: getValues('room_type'),
        });
    }, [mutate, roomId]);

    return (
        <Offcanvas show={show} onHide={handleClose} placement="end" className="offcanvas-responsive">
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>Create Rate</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <RoomTypeMultiSelect control={control} errors={errors} />

                    {/* Name */}
                    <div className="row mb-3 align-items-center">
                        <label htmlFor="name" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Rate Name: </label>
                        <div className="col-sm-8">
                            <input
                                type="text"
                                className={clsx("form-control app-form-control rounded-1", errors?.name && 'is-invalid')}
                                id="name"
                                placeholder='Enter rate name'
                                {...register('name')}
                            />
                            {errors?.name && (
                                <div className="invalid-feedback">{typeof errors.name.message === 'string' && errors.name.message}</div>
                            )}
                        </div>
                    </div>

                    {/* Description */}
                    <div className="row mb-3 align-items-center">
                        <label htmlFor="description" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Description: </label>
                        <div className="col-sm-8">
                            <textarea
                                className={clsx("form-control app-form-control rounded-1", errors?.description && 'is-invalid')}
                                id="description"
                                placeholder='Enter rate description'
                                {...register('description')}
                            />
                            {errors?.description && (
                                <div className="invalid-feedback">{typeof errors.description.message === 'string' && errors.description.message}</div>
                            )}
                        </div>
                    </div>

                    <h3 className="mt-6 border-bottom fw-normal">Rate Settings</h3>
                    {/* Max Night */}
                    <div className="row mb-3 align-items-center">
                        <label htmlFor="max_night" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Max Night: </label>
                        <div className="col-sm-8">
                            <input
                                type="number"
                                className={clsx("form-control app-form-control rounded-1", errors?.max_night && 'is-invalid')}
                                id="max_night"
                                placeholder='Enter max night'
                                {...register('max_night')}
                            />
                            {errors?.max_night && (
                                <div className="invalid-feedback">{typeof errors.max_night.message === 'string' && errors.max_night.message}</div>
                            )}
                        </div>
                    </div>

                    {/* Min Night */}
                    <div className="row mb-3 align-items-center">
                        <label htmlFor="min_night" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Min Night: </label>
                        <div className="col-sm-8">
                            <input
                                type="number"
                                className={clsx("form-control app-form-control rounded-1", errors?.min_night && 'is-invalid')}
                                id="min_night"
                                placeholder='Enter min night'
                                {...register('min_night')}
                            />
                            {errors?.min_night && (
                                <div className="invalid-feedback">{typeof errors.min_night.message === 'string' && errors.min_night.message}</div>
                            )}
                        </div>
                    </div>

                    {/* guest_include */}
                    <div className="row mb-3 align-items-center">
                        <label htmlFor="guest_include" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Guest Include: </label>
                        <div className="col-sm-8">
                            <input
                                type="number"
                                className={clsx("form-control app-form-control rounded-1", errors?.guest_include && 'is-invalid')}
                                id="guest_include"
                                placeholder='Enter guest include'
                                {...register('guest_include')}
                            />
                            {errors?.guest_include && (
                                <div className="invalid-feedback">{typeof errors.guest_include.message === 'string' && errors.guest_include.message}</div>
                            )}
                        </div>
                    </div>

                    {/* guest_max */}
                    <div className="row mb-3 align-items-center">
                        <label htmlFor="guest_max" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Guest Max: </label>
                        <div className="col-sm-8">
                            <input
                                type="number"
                                className={clsx("form-control app-form-control rounded-1", errors?.guest_max && 'is-invalid')}
                                id="guest_max"
                                placeholder='Enter guest max'
                                {...register('guest_max')}
                            />
                            {errors?.guest_max && (
                                <div className="invalid-feedback">{typeof errors.guest_max.message === 'string' && errors.guest_max.message}</div>
                            )}
                        </div>
                    </div>

                    {/* extra_adult */}
                    <div className="row mb-3 align-items-center">
                        <label htmlFor="extra_adult" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Extra Adult: </label>
                        <div className="col-sm-8">
                            <input
                                type="number"
                                className={clsx("form-control app-form-control rounded-1", errors?.extra_adult && 'is-invalid')}
                                id="extra_adult"
                                placeholder='Enter extra adult'
                                {...register('extra_adult')}
                            />
                            {errors?.extra_adult && (
                                <div className="invalid-feedback">{typeof errors.extra_adult.message === 'string' && errors.extra_adult.message}</div>
                            )}
                        </div>
                    </div>

                    {/* extra_child */}
                    <div className="row mb-3 align-items-center">
                        <label htmlFor="extra_child" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Extra Child: </label>
                        <div className="col-sm-8">
                            <input
                                type="number"
                                className={clsx("form-control app-form-control rounded-1", errors?.extra_child && 'is-invalid')}
                                id="extra_child"
                                placeholder='Enter extra child'
                                {...register('extra_child')}
                            />
                            {errors?.extra_child && (
                                <div className="invalid-feedback">{typeof errors.extra_child.message === 'string' && errors.extra_child.message}</div>
                            )}
                        </div>
                    </div>

                    <h3 className="mt-6 border-bottom fw-normal">Rate Price Settings</h3>
                    {/* extra_adult_rate */}
                    <div className="row mb-3 align-items-center">
                        <label htmlFor="extra_adult_rate" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Extra Adult Rate: </label>
                        <div className="col-sm-8">
                            <Controller
                                name="extra_adult_rate" // Nama field yang diatur oleh React Hook Form
                                control={control}
                                render={({ field }) => (
                                    <CurrencyInput
                                        {...field} // Menghubungkan props ke CurrencyInput
                                        value={field.value} // Nilai dari React Hook Form
                                        onChange={(e, rawValue) => {
                                            e.preventDefault();
                                            field.onChange(rawValue);
                                        }} // Ambil angka murni dari CurrencyInput
                                        error={typeof errors.extra_adult_rate?.message === 'string' ? errors.extra_adult_rate.message : undefined} // Tampilkan pesan error jika ada
                                        placeholder="Enter extra adult rate in Rupiah"
                                    />
                                )}
                            />
                        </div>
                    </div>

                    {/* extra_child_rate */}
                    <div className="row mb-3 align-items-center">
                        <label htmlFor="extra_child_rate" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Extra Child Rate: </label>
                        <div className="col-sm-8">
                            <Controller
                                name="extra_child_rate" // Nama field yang diatur oleh React Hook Form
                                control={control}
                                render={({ field }) => (
                                    <CurrencyInput
                                        {...field} // Menghubungkan props ke CurrencyInput
                                        value={field.value} // Nilai dari React Hook Form
                                        onChange={(e, rawValue) => {
                                            e.preventDefault();
                                            field.onChange(rawValue);
                                        }} // Ambil angka murni dari CurrencyInput
                                        error={typeof errors.extra_child_rate?.message === 'string' ? errors.extra_child_rate.message : undefined} // Tampilkan pesan error jika ada
                                        placeholder="Enter extra child rate in Rupiah"
                                    />
                                )}
                            />
                        </div>
                    </div>

                    {/* min_rate */}
                    <div className="row mb-3 align-items-center">
                        <label htmlFor="min_rate" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Min Rate: </label>
                        <div className="col-sm-8">
                            <Controller
                                name="min_rate" // Nama field yang diatur oleh React Hook Form
                                control={control}
                                render={({ field }) => (
                                    <CurrencyInput
                                        {...field} // Menghubungkan props ke CurrencyInput
                                        value={field.value} // Nilai dari React Hook Form
                                        onChange={(e, rawValue) => {
                                            e.preventDefault();
                                            field.onChange(rawValue);
                                        }} // Ambil angka murni dari CurrencyInput
                                        error={typeof errors.min_rate?.message === 'string' ? errors.min_rate.message : undefined} // Tampilkan pesan error jika ada
                                        placeholder="Enter min rate in Rupiah"
                                    />
                                )}
                            />
                        </div>
                    </div>

                    {/* rate */}
                    <div className="row mb-3 align-items-center">
                        <label htmlFor="rate" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Rate: </label>
                        <div className="col-sm-8">
                            <Controller
                                name="rate" // Nama field yang diatur oleh React Hook Form
                                control={control}
                                render={({ field }) => (
                                    <CurrencyInput
                                        {...field} // Menghubungkan props ke CurrencyInput
                                        value={field.value} // Nilai dari React Hook Form
                                        onChange={(e, rawValue) => {
                                            e.preventDefault();
                                            field.onChange(rawValue);
                                        }} // Ambil angka murni dari CurrencyInput
                                        error={typeof errors.rate?.message === 'string' ? errors.rate.message : undefined} // Tampilkan pesan error jika ada
                                        placeholder="Enter rate in Rupiah"
                                    />
                                )}
                            />
                        </div>
                    </div>

                    <div className="form-group mt-auto d-grid">
                        <button type="submit" className="btn btn-primary btn-block" disabled={isPending}>
                            {isPending ? 'Creating...' : 'Create Rateplan'}
                        </button>
                    </div>
                </form>
            </Offcanvas.Body>
        </Offcanvas>
    )
}

export default CreateRateDialog

interface RoomTypeMultiSelectProps {
    control: Control<any>;
    errors: FieldErrors<any>;
}

const RoomTypeMultiSelect = ({ control, errors }: RoomTypeMultiSelectProps) => {

    // Fetch data room type
    const rooms = useQuery({
        queryKey: ['rooms_type_multi_select'],
        queryFn: () => privateApi.get('/v4/room-type', {
            params: {
                all: 1
            },
        }).then((res) => res.data.data)
    })

    return (
        <div className="row mb-3 align-items-center">
            <label htmlFor="room_type" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Room Type: </label>
            <div className="col-sm-8">
                <Controller
                    name="room_type"
                    control={control}
                    render={({ field }) => (
                        <Select
                            {...field}
                            isMulti
                            value={
                                rooms.data
                                    ?.filter((roomType: { id: string; name: string }) =>
                                        Array.isArray(field.value)
                                            ? field.value.includes(roomType.id) // Cocokkan ID untuk array (isMulti)
                                            : roomType.id === field.value // Cocokkan untuk single select
                                    )
                                    ?.map((roomType: { id: string; name: string }) => ({
                                        value: roomType.id,
                                        label: roomType.name,
                                    }))
                            }
                            options={rooms.data?.map((roomType: { id: string; name: string }) => ({
                                value: roomType.id,
                                label: roomType.name,
                            }))}
                            placeholder="Select room type"
                            className={clsx(
                                "app-form-control rounded-1",
                                errors?.room_type && "is-invalid"
                            )}
                            onChange={(selectedOptions) => {
                                const selectedValues = selectedOptions?.map((option) => option.value) || [];
                                field.onChange(selectedValues); // Perbarui nilai ke array ID
                            }}
                        />
                    )}
                />
                {errors?.room_type && (
                    <div className="invalid-feedback">{typeof errors.room_type.message === 'string' && errors.room_type.message}</div>
                )}
            </div>
        </div>
    )
}